import { CombatExecutor } from "@/core/battle/CombatExecutor";
import { ControlStateMachine } from "@/core/battle/ControlStateMachine";
import { EntityRegistry } from "@/core/battle/EntityRegistry";
import { ExecutorManager } from "@/core/battle/executors/ExecutorManager";
import { HighlightManager } from "@/core/battle/HighlightManager";
import { MovementExecutor } from "@/core/battle/MovementExecutor";
import { SkillManager } from "@/core/battle/SkillManager";
import { TurnManager } from "@/core/battle/TurnManager";
import { HEX_HIGHLIGHT_COLOR } from "@/core/constants/battle.constants";
import { BattleStatus, ControlState, FactionType } from "@/core/types/battle.types";
import { pauseToggle } from "@/core/utils/battle.utils";
import { coordsToId } from "@/core/utils/hex.utils";
import { BattleBus } from "@/events/Events";
import { getActivePool, isUnitFinished } from "@/store/battleSelectors";
import { useBattleStore } from "@/store/useBattleStore";

/**
 * Handles all user input (hover, click, mouse)
 * Maintains input state machine
 */
export class InputController {
    private hotkeysInitialized = false;

    
    constructor(
        private highlightManager: HighlightManager,
        private executorManager: ExecutorManager,
        private entityRegistry: EntityRegistry,
        private stateMachine: ControlStateMachine,
        private skillManager: SkillManager,
        private turnManager: TurnManager,
    ) {
        this.setupEventHandlers();
        // this.setupHotkeys();
    }

    public initializeHotkeys(): void {
        if (this.hotkeysInitialized) return;
        this.setupHotkeys();
        this.hotkeysInitialized = true;
    }

    private setupHotkeys(): void {
        this.onKeyDown = this.onKeyDown.bind(this); // важливо
        window.addEventListener('keydown', this.onKeyDown);
        
    }


    // --- ПРИВ'ЯЗАНІ ОБРОБНИКИ ДЛЯ OFF ---
    private handleHexClickBound = (data: { id: string}) => this.handleHexClick(data.id);
    // private handleHexHoverBound = (data: { id: string; position: { x: number; y: number } }) => this.handleHexHover(data.id);
    // private handleHexOutBound = (data: { id: string}) => this.handleHexOut(data.id);
    
    private handleUnitClickBound = (data: { id: string}) => this.handleUnitClick(data.id);
    // private handleUnitHoverBound = (data: { id: string; position: { x: number; y: number } }) => this.handleUnitHover(data.id);
    // private handleUnitOutBound = (data: { id: string}) => this.handleUnitOut(data.id);

    // private handleMouseMoveBound = (position: { x: number; y: number }) => this.handleMouseMove(position);
    
    private setupEventHandlers(): void {
        // BattleBus.on('HEX_HOVER', this.handleHexHoverBound);
        // BattleBus.on('HEX_OUT', this.handleHexOutBound);
        BattleBus.on('HEX_CLICK', this.handleHexClickBound);
        
        // BattleBus.on('UNIT_HOVER', this.handleUnitHoverBound);
        // BattleBus.on('UNIT_OUT', this.handleUnitOutBound);
        BattleBus.on('UNIT_CLICK', this.handleUnitClickBound);
        // BattleBus.on('MOUSE_MOVE', this.handleMouseMoveBound);
        // BattleBus.on('SKILL_EXECUTED', this.skillExecutedBound);   
    }

    /**
     * Checks if click interactions are allowed
     */
    private canHandleClick(): boolean {
        return this.stateMachine.canHandleInput(useBattleStore.getState().status);
    }

    // --- EVENT HANDLERS ---

    private handleHexClick(hexId: string): void {
        
        if (!this.canHandleClick()) return;

        const hexVisual = this.entityRegistry.getHex(hexId);

        if (!hexVisual) return;

        const { 
            selectedUnitId, selectedSkillId, 
            selectedPath, battleMap 
        } = useBattleStore.getState();
        
        if (!selectedUnitId) return;

        const hex = battleMap.get(hexId);
        if (!hex) return;

        // If hex has a unit - delegate to unit click logic
        if (hex.entityId) {
            this.handleUnitClick(hex.entityId);
            return;
        }

        // SKILL MODE: Don't allow movement, skill highlights stay visible
        if (selectedSkillId) {
            // Could add feedback here: "Cancel skill first" or "Invalid target"
            return;
        }

        // MOVEMENT MODE: Handle movement logic
        // this.highlightManager.resetAllHexes(); // Only clear when in movement mode

        if (selectedPath.lastStep?.id === hex.id) {
            // useBattleStore.getState().resetSelectedPath();
            useBattleStore.getState().setSelectedHex(null);
            this.executorManager.move(selectedUnitId, selectedPath);
            return;
        }

        // this.highlightManager.flashHex(hexId);
        useBattleStore.getState().setSelectedHex(hexId);
        
        // Custom hex click logic goes here
    }

    private handleUnitClick(unitId: string): void {
        if (!this.canHandleClick()) return;
        
        const state = useBattleStore.getState();
        const { 
            selectedUnitId, 
            selectedSkillId, 
        } = state;

        const isEnemy = state.units.get(unitId)?.faction !== FactionType.Player;

        // If unit and skill selected - attempt attack
        if (selectedUnitId && selectedSkillId && isEnemy) {
            this.executorManager.attack(selectedUnitId, unitId, selectedSkillId);
            return;
        }
        const currentPool = getActivePool(state);
        
        if (currentPool?.faction === FactionType.Player && currentPool.unitIds.includes(unitId) && !isUnitFinished(state, unitId) && this.stateMachine.getState() !== ControlState.SkillTargeting) {
            state.setSelectedUnit(unitId);
            state.resetSelectedPath();
        }

        useBattleStore.getState().setInspectedUnit(unitId);
        
        // Custom unit click logic goes here
    }

    private onKeyDown(e: KeyboardEvent) {
        
        // Number keys 1-9 for skills
        if (e.key >= '1' && e.key <= '9') {
            const skillIndex = parseInt(e.key) - 1;
            this.selectSkillByIndex(skillIndex);
        }
        if (e.code === 'KeyF') {
            const { status, selectedUnitId, units } = useBattleStore.getState();
            if (!this.stateMachine.canHandleInput(status)) return;
            if (!selectedUnitId) return;
            
            this.turnManager.endUnitTurn(selectedUnitId);
        }
        
        // ESC to cancel skill
        if (e.key === 'Escape') {
            e.preventDefault();
            const state = useBattleStore.getState();

            if (state.selectedSkillId) {
                if (!this.canHandleClick()) return;
                this.skillManager.deselectSkill();
                return;
            } else if (state.selectedHexId) {
                if (!this.canHandleClick()) return;
                state.setSelectedHex(null);
                state.resetSelectedPath();
                return;
            } else if (state.inspectedUnitId) {
                if (!this.canHandleClick()) return;
                state.setInspectedUnit(null);
                return;
            }
            pauseToggle(state);
        }

        if (e.code === 'Space') {
            e.preventDefault();
            const selectedUnitId = useBattleStore.getState().selectedUnitId;
            if (selectedUnitId) {
                BattleBus.emit('FOCUS_ON_UNIT', {unitId: selectedUnitId, force: true});
            }
        }
        
    }

    private selectSkillByIndex(index: number): void {
        const state = useBattleStore.getState();
        const selectedUnitId = state.selectedUnitId;
        if (!selectedUnitId) return;
        
        const unit = state.units.get(selectedUnitId);
        if (!unit || !unit.skills[index]) return;
        
        const skillId = unit.skills[index].id;
        this.skillManager.toggleSkill(skillId);
        
    }
    
    
    destroy(): void {
        window.removeEventListener('keydown', this.onKeyDown);
        // 2. Відписка від шини
        BattleBus.off('HEX_CLICK', this.handleHexClickBound);
        BattleBus.off('UNIT_CLICK', this.handleUnitClickBound);
        // BattleBus.off('HEX_HOVER', this.handleHexHoverBound);
        
        // BattleBus.off('HEX_OUT', this.handleHexOutBound);
        // BattleBus.off('UNIT_HOVER', this.handleUnitHoverBound);
        
        // BattleBus.off('UNIT_OUT', this.handleUnitOutBound);
        // BattleBus.off('MOUSE_MOVE', this.handleMouseMoveBound);
    }
}